import { inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, EMPTY, Subject, switchMap } from 'rxjs';

import { markdownToSpokenText } from '../../../../shared/models/markdown-text.model';
import type { MessageSender } from '../../../../shared/models/message-sender.model';
import { emptyWaveform } from '../../../../shared/models/voice-waveform.model';
import { AUDIO_PLAYBACK_PROVIDER, SYNTHESIS_PROVIDER } from '../../tokens/chat.tokens';
import type { SpeechRequest } from '../providers/i-synthesis.provider';
import type { IncomingSpeech, IVoiceReadingHandler } from './i-voice-reading.handler';

export const DEFAULT_VOICE_READING_RATE = 1;

/**
 * The operator station was the only listener before the citizen conversation got its own settings, and it
 * reads the citizen: keeping that as the default means a handler nobody configured behaves as it did.
 */
const DEFAULT_READ_FROM: readonly MessageSender[] = ['citizen'];

@Injectable()
export class VoiceReadingHandler implements IVoiceReadingHandler {
  private readonly synthesisProvider = inject(SYNTHESIS_PROVIDER);
  private readonly playbackProvider = inject(AUDIO_PLAYBACK_PROVIDER);

  private readonly _enabled = signal(false);
  readonly enabled = this._enabled.asReadonly();

  private readonly _speakingMessageId = signal<string | null>(null);
  readonly speakingMessageId = this._speakingMessageId.asReadonly();

  private readonly _speakingLevels = signal<readonly number[]>(emptyWaveform());
  readonly speakingLevels = this._speakingLevels.asReadonly();

  private readonly _speakingProgress = signal(0);
  readonly speakingProgress = this._speakingProgress.asReadonly();

  /**
   * La bolla della lettura chiesta per ultima. Non e' lo stato mostrato: lo diventa quando l'audio comincia
   * davvero a suonare, perche' fra la richiesta e il primo suono c'e' la sintesi — e una bolla che perde il
   * proprio testo mentre non si sente ancora niente sembra rotta.
   */
  private pendingMessageId: string | null = null;

  private voiceName: string | null = null;
  private rate = DEFAULT_VOICE_READING_RATE;
  private readFrom: readonly MessageSender[] = DEFAULT_READ_FROM;

  /**
   * La coda delle letture. Un `null` non e' una richiesta ma una rinuncia: passa nello stesso
   * `switchMap` e ne annulla quella in volo — senza, una lettura fermata mentre la sintesi era ancora in
   * corso si sarebbe messa a parlare subito dopo, quando chi ascolta l'aveva appena zittita.
   */
  private readonly requests = new Subject<SpeechRequest | null>();

  constructor() {
    this.requests
      .pipe(
        // Una lettura nuova sostituisce quella ancora in volo: senza switchMap l'audio del messaggio
        // precedente arriverebbe dopo e parlerebbe sopra quello appena chiesto.
        switchMap((request) =>
          request === null
            ? EMPTY
            : this.synthesisProvider.synthesize(request).pipe(
                // Una lettura che non arriva non interrompe la conversazione: chi sta scrivendo non deve
                // vedere un errore perché il servizio vocale ha risposto male.
                catchError(() => EMPTY),
              ),
        ),
        takeUntilDestroyed(),
      )
      .subscribe((audio) => this.playbackProvider.play(audio));

    this.playbackProvider.playback$.pipe(takeUntilDestroyed()).subscribe((playback) => {
      this._speakingMessageId.set(playback.playing ? this.pendingMessageId : null);
      this._speakingLevels.set(playback.levels);
      this._speakingProgress.set(playback.progress);
    });
  }

  setEnabled(enabled: boolean): void {
    this._enabled.set(enabled);
  }

  configure(options: {
    voiceName: string | null;
    rate: number;
    readFrom: readonly MessageSender[];
  }): void {
    this.voiceName = options.voiceName;
    this.rate = options.rate;
    this.readFrom = options.readFrom;
  }

  readIncoming(message: IncomingSpeech, language: string): void {
    if (!this._enabled() || !this.readFrom.includes(message.sender)) return;
    // A streamed message reaches the conversation as an empty bubble before its text does: speaking it
    // would produce a silent utterance and consume the message, so it waits for the text.
    if (!message.text) return;

    this.read(message.text, language, message.id);
  }

  read(text: string, language: string, messageId?: string): void {
    if (!text) return;
    if (!this.playbackProvider.isAvailable()) return;

    // Il testo di un messaggio viaggia in markdown: e' quello che la bolla mostra come formato e che la
    // voce, se lo ricevesse cosi', pronuncerebbe come segni. Qui la conversazione entra nella sintesi, ed
    // e' l'unico punto da cui ci entra: la spoglia si fa una volta sola, qui.
    const spoken = markdownToSpokenText(text);
    // Un messaggio fatto di soli marcatori non ha niente da dire: chiedere l'audio di un testo vuoto
    // costerebbe un giro dal BFF per un silenzio.
    if (!spoken.trim()) return;

    this.playbackProvider.stop();
    this.pendingMessageId = messageId ?? null;
    this.requests.next({
      text: spoken,
      language,
      voiceName: this.voiceName,
      rate: this.rate,
    });
  }

  stop(): void {
    this.pendingMessageId = null;
    // Prima la sintesi ancora in volo, poi l'audio che suona: fermare solo il secondo lascerebbe arrivare
    // il primo un istante dopo.
    this.requests.next(null);
    this.playbackProvider.stop();
  }
}
